import { compareKey } from '@thinkalpha/table-client';
import type { Key } from '@thinkalpha/table-client';
import type { TableClass } from '@thinkalpha/ui-types/contracts/workspace';

export interface TableTableOption {
    label: string;
    normalizedLabel: string;
    value: string;
}

export interface TableSelectionState {
    table: Key | null;
    tableClass: TableClass | null;
    tableKeyOptions: readonly TableTableOption[];
    searchQuery: string;
    tableSettingsOpen: boolean;
}

export interface TableSelectionPersistedState {
    table: Key | null;
    class: TableClass | null;
}

export type TableSelectionAction =
    | { type: 'SET_TABLE_KEY_OPTIONS'; options: readonly TableTableOption[] }
    | { type: 'SET_SEARCH_QUERY'; query: string }
    | { type: 'SELECT_TABLE'; table: Key }
    | { type: 'SET_TABLE_CLASS'; tableClass: TableClass | null }
    | { type: 'SET_TABLE_SETTINGS_OPEN'; open: boolean };

function sameOptions(left: readonly TableTableOption[], right: readonly TableTableOption[]): boolean {
    return left.length === right.length && left.every((option, index) => option.value === right[index]?.value);
}

export function tableTableSelectionReducer(
    state: TableSelectionState,
    action: TableSelectionAction,
): TableSelectionState {
    switch (action.type) {
        case 'SET_TABLE_KEY_OPTIONS':
            if (sameOptions(state.tableKeyOptions, action.options)) return state;
            return { ...state, tableKeyOptions: action.options };
        case 'SET_SEARCH_QUERY':
            if (state.searchQuery === action.query) return state;
            return { ...state, searchQuery: action.query };
        case 'SELECT_TABLE':
            if (
                state.table !== null &&
                compareKey(state.table, action.table) &&
                state.searchQuery === '' &&
                !state.tableSettingsOpen
            ) {
                return state;
            }
            return {
                ...state,
                table: action.table,
                searchQuery: '',
                tableSettingsOpen: false,
            };
        case 'SET_TABLE_CLASS':
            if (state.tableClass === action.tableClass) return state;
            return { ...state, tableClass: action.tableClass };
        case 'SET_TABLE_SETTINGS_OPEN':
            if (state.tableSettingsOpen === action.open) return state;
            return {
                ...state,
                tableSettingsOpen: action.open,
                searchQuery: action.open ? '' : state.searchQuery,
            };
        default: {
            const _exhaustiveCheck: never = action;
            return _exhaustiveCheck;
        }
    }
}
